import { useRef, useState } from "react";
import {
  Home,
  Briefcase,
  Recycle,
  Truck,
  Trash2,
  Snowflake,
  Container,
  ArrowUpRight,
  ArrowLeft,
  X,
} from "lucide-react";
import { buildWhatsAppUrl } from "../../lib/constants";
import { useInView, revealClass } from "../../hooks/useInView";

const BREMER_SCHNEEMANN_URL = "https://www.bremer-schneemann.de/";

const SERVICES = [
  {
    id: "haushaltsaufloesung",
    icon: Home,
    title: "Haushaltsauflösung & Entrümpelung",
    tag: "Festpreis",
    subtitle: "Wohnungen · Häuser · Messie · Nachlass",
    desc:
      "Komplette Räumung — von der ersten Schraube bis zur besenreinen Übergabe. Diskret, schnell, fair.",
    bullets: [
      "Wohnung / Haus",
      "Messie-Entrümpelung",
      "Nachlass-Räumung",
      "Keller & Dachboden",
    ],
  },
  {
    id: "geschaeftsaufloesung",
    icon: Briefcase,
    title: "Geschäfts- & Betriebsauflösung",
    tag: "Diskret · B2B",
    subtitle: "Büros · Lager · Werkstätten · Praxen",
    desc:
      "Komplette Auflösung von Geschäftsräumen, Lagern und Werkstätten — zuverlässig, dokumentiert, termingerecht.",
    bullets: ["Büro & Praxis", "Lager & Werkstatt", "Termin nach Absprache", "Auf Wunsch nach Feierabend"],
  },
  {
    id: "schrott",
    icon: Recycle,
    title: "Kostenlose Schrott- & Altmetallabholung",
    tag: "0 € · Gratis",
    subtitle: "Eisen · Kupfer · Alu · Messing · Ankauf",
    desc:
      "Wir holen Metallschrott jeglicher Art kostenlos bei Ihnen ab — Bremen & Umzu. Ankauf von NE-Metallen zu Tagespreisen.",
    bullets: [
      "Eisen, Stahl, Edelstahl",
      "Kupfer, Messing, Alu",
      "Ankauf nach Tagespreis",
      "Heizkörper bis Lagerhalle",
    ],
  },
  {
    id: "transport",
    icon: Truck,
    title: "Transport von A nach B",
    tag: "Schnell · Sicher",
    subtitle: "Einzelstücke · Umzüge · Sondertransporte",
    desc:
      "Ein Möbelstück oder kompletter Umzug — eigener Transporter & Anhänger, oft am gleichen Tag.",
    bullets: ["Einzelne Möbel", "Kleiner Umzug", "Sondertransport", "Eigene Flotte"],
  },
  {
    id: "entsorgung",
    icon: Trash2,
    title: "Entsorgung jeglicher Art",
    tag: "Mit Nachweis",
    subtitle: "Sperrmüll · Holz · Bauschutt · Asbest …",
    desc:
      "Fachgerechte Entsorgung mit schriftlichem Nachweis. Klicken Sie & wählen Sie, was weg soll.",
    expandable: true,
    sub: [
      "Sperrmüll Abholung",
      "Holz",
      "Bauschutt",
      "Baumischabfall",
      "Gartenabfall",
      "Glaswolle",
      "Asbest",
      "Elektroschrott",
      "Sondermüll",
    ],
  },
  {
    id: "winterdienst",
    icon: Snowflake,
    title: "Winterdienst",
    tag: "Bremer Schneemann",
    subtitle: "Schneeräumung · Streudienst · Bremen",
    desc:
      "Schneeräumung & Streudienst für Privat und Gewerbe — direkt bei unserem Partner bremer-schneemann.de.",
    bullets: ["Gehwege & Einfahrten", "Firmengelände", "24/7 im Winter", "Eigenständige Marke"],
    externalUrl: BREMER_SCHNEEMANN_URL,
  },
];

export const Services = () => {
  const [headRef, headIn] = useInView();
  const [openSub, setOpenSub] = useState(null);

  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative py-16 md:py-24 bg-white overflow-hidden"
    >
      <div className="relative px-4 md:px-8 lg:px-12">
        <div
          ref={headRef}
          {...revealClass(headIn)}
          className="grid lg:grid-cols-12 gap-8 mb-12 md:mb-16"
        >
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-black/45">
              // 02 — Unsere Leistungen
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[68px] font-semibold tracking-[-0.04em] text-black leading-[1.02]">
              Eine Anlaufstelle.
              <br />
              <span className="text-black/35">Alles in einem.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-black/60 text-base lg:text-lg max-w-md">
              Sieben Leistungen, ein Team, ein Anruf. Was raus muss — geht raus.
              Was weg muss — ist weg. Schnell, ehrlich, fair.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <ServiceCard
              key={s.id}
              {...s}
              index={i}
              onExpand={() => setOpenSub(s)}
            />
          ))}
        </div>
      </div>

      {/* Subcategory modal for Entsorgung */}
      {openSub && (
        <SubcategoryModal service={openSub} onClose={() => setOpenSub(null)} />
      )}
    </section>
  );
};

const ServiceCard = ({
  icon: Icon,
  title,
  subtitle,
  desc,
  tag,
  id,
  bullets = [],
  expandable,
  sub,
  externalUrl,
  index,
  onExpand,
}) => {
  const [ref, inView] = useInView();
  const innerRef = useRef(null);

  const href = expandable
    ? "#"
    : externalUrl ||
      buildWhatsAppUrl(`Hallo BRS, ich interessiere mich für: ${title}`);

  const handleClick = (e) => {
    if (expandable) {
      e.preventDefault();
      onExpand();
    }
  };

  return (
    <a
      ref={ref}
      {...revealClass(inView, (index % 3) * 0.07)}
      href={href}
      target={expandable ? undefined : "_blank"}
      rel="noreferrer"
      onClick={handleClick}
      data-testid={`service-card-${id}`}
      data-cursor="hover"
      className="group block"
    >
      <div
        ref={innerRef}
        className="relative rounded-3xl p-6 md:p-7 border border-black/10 bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-[8px_8px_0_rgba(0,0,0,1)] h-full flex flex-col"
      >
        <div className="relative flex items-start justify-between">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45">
              // 0{index + 1}
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full self-start bg-black/[0.04] text-black border border-black/10">
              {tag}
            </span>
          </div>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-black text-white transition-transform duration-300 group-hover:rotate-[-6deg] group-hover:scale-110">
            <Icon className="w-5 h-5" />
          </div>
        </div>

        <h3 className="relative font-display text-2xl md:text-[26px] font-semibold tracking-[-0.025em] leading-[1.1] mt-7">
          {title}
        </h3>
        {subtitle && (
          <div className="mt-1.5 text-[#0a8f3f] text-sm font-medium">
            {subtitle}
          </div>
        )}
        <p className="relative mt-3 text-sm leading-relaxed text-black/60">
          {desc}
        </p>

        <div className="relative mt-5 flex flex-wrap gap-1.5">
          {(bullets.length ? bullets : sub?.slice(0, 4) || []).map((b, i) => (
            <span
              key={i}
              className="text-[11px] px-2.5 py-1 rounded-full bg-black/[0.04] text-black/70 border border-black/10"
            >
              {b}
            </span>
          ))}
          {expandable && sub && (
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-black text-white">
              + {sub.length - 4} weitere
            </span>
          )}
        </div>

        <div className="relative mt-auto pt-6 border-t border-black/8 flex items-center justify-between">
          <span className="text-sm font-medium">
            {expandable
              ? "Kategorie wählen"
              : externalUrl
              ? "Zur Partnerseite"
              : "Per WhatsApp anfragen"}
          </span>
          <span className="w-10 h-10 rounded-full border border-black/15 flex items-center justify-center transition-all group-hover:bg-black group-hover:text-white group-hover:border-black group-hover:rotate-45">
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </a>
  );
};

/* =========================================================
   Subcategory modal — for Entsorgung
========================================================= */
const SubcategoryModal = ({ service, onClose }) => {
  const Icon = service.icon;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-0 md:p-6 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="entsorgung-modal"
    >
      <div
        className="relative w-full md:max-w-lg bg-white rounded-t-[28px] md:rounded-[28px] border-2 border-black overflow-hidden shadow-[12px_12px_0_rgba(37,211,102,1)] step-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 md:px-7 pt-6 pb-4 border-b border-black/10 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-black text-white flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/50">
                Was soll entsorgt werden?
              </div>
              <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight mt-0.5">
                {service.title}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="entsorgung-modal-close"
            aria-label="Schließen"
            className="w-9 h-9 rounded-full bg-black/[0.05] hover:bg-black hover:text-white flex items-center justify-center shrink-0 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <div className="px-5 md:px-7 py-5 md:py-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
          {service.sub.map((opt) => (
            <a
              key={opt}
              href={buildWhatsAppUrl(`Hallo BRS, ich brauche Entsorgung: ${opt}`)}
              target="_blank"
              rel="noreferrer"
              data-testid={`entsorgung-opt-${opt}`}
              className="group flex items-center justify-between gap-3 h-12 px-4 rounded-2xl border-2 border-black/15 hover:border-black hover:bg-black hover:text-white transition-all"
            >
              <span className="text-sm font-medium">{opt}</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 md:px-7 py-4 bg-black/[0.02] border-t border-black/10 flex items-center justify-between gap-3 text-xs text-black/55">
          <span className="flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" />
            Auswahl öffnet WhatsApp
          </span>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase">
            Kostenlos · 0 €
          </span>
        </div>
      </div>
    </div>
  );
};
